import { clients } from '../clients/grpcClients';
import { useLocalStorage } from '../stores/localStorage';
import {
  CreateAccountRequest,
  CreateAccountResponse,
  GetAccountRequest,
  GetAccountResponse,
  SetAccountStatusRequest,
  SetAccountStatusResponse,
  UB_Account,
  UB_Account_User,
  UpdateAccountRequest,
  UpdateAccountResponse,
} from './protosCompiled/ub_account/ub_account_pb';
import {
  LogAccountUserInorOutRequest,
  LogAccountUserInorOutResponse,
  LogAccountUserOutRequest,
  LogAccountUserOutResponse,
} from './protosCompiled/ub_users/ub_users_pb';

export class UbAccountService {
  private static getMetadata() {
    const sessionToken = useLocalStorage.getState().sessionToken;
    if (!sessionToken) {
      console.error('Session token is missing!');
    }
    return {
      sessionToken,
    };
  }

  public static createAccount = (
    data: UB_Account.AsObject
  ): Promise<CreateAccountResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const account = new UB_Account();
      account.setAccountname(data.accountname);
      account.setAccountemail(data.accountemail);
      account.setAccountphone(data.accountphone);
      account.setNumusers(data.numusers);
      account.setAccountisactive(data.accountisactive);

      const account_user = new UB_Account_User();
      account_user.setUsername(data.user?.username || '');
      account_user.setUseremail(data.user?.useremail || '');
      account_user.setUserisactive(data.user?.userisactive || true);
      account_user.setUserpassword(data.user?.userpassword || '');
      account_user.setUserphotourl(data.user?.userphotourl || '');
      account_user.setRoleid(data.user?.roleid || '');
      account_user.setRolename(data.user?.rolename || '');
      account_user.setIsprimaryadmin(data.user?.isprimaryadmin || true);
      account_user.setIsloggedin(data.user?.isloggedin || false);

      // Create the request object and set the account
      const request = new CreateAccountRequest();
      account.setUser(account_user);
      request.setAccount(account);

      // Make the gRPC call to create the account
      clients.ubAccountServiceClient.createAccount(request, {}, (err: any, response: any) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  };

  public static updateAccount = (
    data: UB_Account.AsObject
  ): Promise<UpdateAccountResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      // Create the UB_Account object and populate it with data
      const account = new UB_Account();
      account.setAccountname(data.accountname);
      account.setAccountemail(data.accountemail);
      account.setAccountphone(data.accountphone);
      account.setNumusers(data.numusers);

      // Create the request object and set the account
      const request = new UpdateAccountRequest();
      request.setAccount(account); // Set the account object

      // Make the gRPC call to update the account
      clients.ubAccountServiceClient.updateAccount(request, {}, (err: any, response: any) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  };

  public static setAccountStatus = (
    data: SetAccountStatusRequest.AsObject
  ): Promise<SetAccountStatusResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new SetAccountStatusRequest();
      request.setSessiontoken(this.getMetadata().sessionToken);
      request.setIsactive(data.isactive);
      request.setNumseats(data.numseats);

      // Make the gRPC call to set the account status
      clients.ubAccountServiceClient.setAccountStatus(request, {}, (err: any, response: any) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  };

  public static logAccountUserInorOut = (
    userId: string,
    isLoggedIn: boolean
  ): Promise<LogAccountUserInorOutResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      // Create the request object and set the userId and isLoggedIn
      const request = new LogAccountUserInorOutRequest();
      request.setUserid(userId);
      request.setIsloggedin(isLoggedIn);
      request.setSessiontoken(this.getMetadata().sessionToken);

      // Make the gRPC call to log the user in or out
      clients.ub_userServiceClient.logAccountUserInorOut(request, {}, (err: any, response: any) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  };

  public static logAccountUserOut = (
    userId: string
  ): Promise<LogAccountUserOutResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new LogAccountUserOutRequest();
      request.setUserid(userId);

      // Make the gRPC call to log the user out
      clients.ub_userServiceClient.logAccountUserOut(request, {}, (err: any, response: any) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  };

  public static getAccount = () => {
    return new Promise<GetAccountResponse.AsObject>((resolve, reject) => {
      const getAccountRequest = new GetAccountRequest();
      getAccountRequest.setSessiontoken(this.getMetadata().sessionToken);
      clients.ubAccountServiceClient.getAccount(
        getAccountRequest,
        {},
        (err: any, response: any) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(response.toObject());
        }
      );
    });
  };
}
